import { Text, Tooltip } from "@chakra-ui/react";
import { StatusTag } from "src/components/DataDisplay/StatusTag";
import { ColumnDefWithTooltip } from "src/components/Table/Table";
import {
  FORM_SCHOOL_RANK_SUB_STATUS,
  FormSchoolRankSubStatusIndex,
} from "src/constants";
import type { OrganizationError } from "src/hooks/useOrganization";
import { calculateOfferRemainingTime } from "src/services/form/offer";
import {
  formatIsoDateToOrgDate,
  formatIsoDateToOrgDateTime,
} from "src/services/format";
import type * as GQL from "src/types/graphql";
import type { RemoteData } from "src/types/remoteData";
import { ColumnId } from "./constants";

type Row = {
  schoolForm: {
    subStatus: string | null;
    subStatusUpdatedAt: timestamptz | null;
    offer: {
      expiresAt: timestamptz | null;
    } | null;
  } | null;
};

export function buildSubStatusColumnDef<T extends Row>({
  header,
  organization,
}: {
  header?: string;
  organization: RemoteData<OrganizationError, GQL.Organization>;
}): ColumnDefWithTooltip<T> {
  return {
    id: ColumnId.SubStatus,
    header: header ?? "Sub-status",
    tooltip: "Sort by sub-status",
    cell: (props) => {
      const schoolForm = props.row.original.schoolForm;
      if (!schoolForm?.subStatus) return null;

      const baseStatus =
        FORM_SCHOOL_RANK_SUB_STATUS[
          schoolForm.subStatus as FormSchoolRankSubStatusIndex
        ];

      // Check if this is an offered substatus with an expiration date
      if (
        schoolForm.subStatus === "Offered" &&
        schoolForm.offer?.expiresAt &&
        organization.hasData()
      ) {
        const remainingTime = calculateOfferRemainingTime({
          expires_at: schoolForm.offer.expiresAt,
          timezone_name: organization.data.timezone_name,
        });

        const customLabel = `${baseStatus.label}, ${remainingTime}d left`;
        const tooltipText = `Offer will expire on ${formatIsoDateToOrgDateTime(
          schoolForm.offer.expiresAt,
          organization
        )}`;

        return (
          <Tooltip
            label={tooltipText}
            placement="top"
            hasArrow
            textAlign="center"
          >
            <span>
              <StatusTag status={{ ...baseStatus, label: customLabel }} />
            </span>
          </Tooltip>
        );
      }

      return <StatusTag status={baseStatus} />;
    },
  };
}

export function buildSubStatusDateColumnDef<T extends Row>({
  organization,
  header,
}: {
  organization: RemoteData<OrganizationError, GQL.Organization>;
  header?: string;
}): ColumnDefWithTooltip<T> {
  return {
    id: ColumnId.SubStatusDate,
    header: header ?? "Sub-status Date",
    tooltip: "Sort by date last updated",
    cell: (props) => {
      const schoolForm = props.row.original.schoolForm;
      if (!schoolForm?.subStatusUpdatedAt) return null;
      return (
        <Text>
          {formatIsoDateToOrgDate(schoolForm.subStatusUpdatedAt, organization)}
        </Text>
      );
    },
  };
}
